import React from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider } from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { router } from './router.ts'
import ErrorBoundary from './components/ErrorBoundary.jsx'
import { ToastProvider } from './context/ToastContext.jsx'
import { initSentry } from './lib/sentry'
import { initDeviceSettings } from './lib/deviceSettings'
import { initReachability } from './lib/reachability'
import { capturePendingCheckToken } from './lib/check/pendingCheckToken'
import { capturePendingTeacherStart } from './lib/teacher/pendingTeacherStart'

// P-OBSERVABILITY-A: initialize Sentry before the app renders so it can
// capture render crashes (via ErrorBoundary.componentDidCatch), plus
// unhandled errors + promise rejections (Sentry's global handlers,
// installed by init). No-op in dev / when VITE_SENTRY_DSN is unset —
// see src/lib/sentry.ts. Render crashes report through the existing
// <ErrorBoundary> rather than a nested <Sentry.ErrorBoundary> so the
// app keeps its Reset-&-reload recovery UI + sessionLog repro trail
// (React routes a crash to the nearest boundary only — a Sentry
// boundary would have to replace that UX to capture). See PR body.
initSentry()

// MA-1044: keep a free readiness check's token from /onboarding?check=<token>
// before the gate ladder (sign-in, sign-up) can drop the query string. The
// onboarding check-in reuses it after a server verify (onboarding.diagnostic).
capturePendingCheckToken(window.location)
// MA-1056: the same for the website's "Set up a free class" entry,
// /teacher/start?src=<tag> (src/lib/teacher/pendingTeacherStart.ts).
capturePendingTeacherStart(window.location)

// Apply device-level accessibility prefs (reduce motion / high contrast) to
// <html> BEFORE first paint so there's no flash, and keep them synced across tabs.
initDeviceSettings()

// Phase G2: Tailwind 4 + design tokens. Loaded once here so every
// route + shadcn primitive has classes available. Preflight is
// disabled in this file (theme + utilities layers only) per §G2-Q2
// to protect legacy inline-style screens — see comments in
// src/styles/globals.css.
// C1/C2: self-hosted fonts (registers @font-face before first paint).
import './styles/fonts'
import './styles/globals.css'

// Phase G1: TanStack Router replaces the App.jsx switch-statement.
// The router mounts <__root> which mounts <App />, which runs the
// gate ladder and renders <Outlet /> when gates pass. ToastProvider +
// ErrorBoundary wrap the router so toasts + crash recovery apply to
// every route.

// Phase G3 Stage 1a: React Query provider wraps the router so
// engine-hook mocks (src/lib/engine/*) can call useQuery during
// Stage 1b screen rendering. Single client instance — staleTime
// defaults are per-hook.
// P0 (Batch 1.5) — resilient defaults so flaky/captive school wifi can't trigger
// a retry storm (the round-6 ERR_INSUFFICIENT_RESOURCES splash-lock). retry:2
// with exponential backoff (was the default 3, unbounded socket churn);
// networkMode:'offlineFirst' so an enabled query RUNS once and FAILS instead of
// pausing forever (the permanent-splash root cause); staleTime 30s + no
// refetch-on-focus to dedupe. initReachability() then makes onlineManager
// reflect a real ping, not bare navigator.onLine.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      networkMode: 'offlineFirst',
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
    mutations: { networkMode: 'offlineFirst', retry: 1 },
  },
})
initReachability()

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
)
